import * as React from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import {
  EMBROIDERY_TYPES,
  EmbroideryTypeKey,
} from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import { useEmbroideryAvailability } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryAvailabilityProvider'
import { useEmbroideryPrices } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { unavailabilityLabel } from '../PlacementPicker/placementLabels'

import { PanelOptionRow } from './PanelOptionRow'

export type EmbroideryOptionRowsProps = {
  onOpenType(typeKey: EmbroideryTypeKey): void
}

/**
 * The embroidery rows of the options panel — one per type the garment offers, each showing
 * what it would cost or what it has already added.
 *
 * A type the garment has nowhere to put is left out entirely rather than drawn greyed: that is the
 * matrix's `offered`, and it does not change as the customer chooses things. `canAdd` is what
 * greys a row, and only while nothing of that type is chosen — a customer who already has text
 * must still be able to open it and edit what they have.
 */
export const EmbroideryOptionRows: React.FC<EmbroideryOptionRowsProps> = ({ onOpenType }) => {
  const { matrix } = useEmbroideryAvailability()
  const { items, removeItem } = useEmbroiderySelection()
  const { formatPrice, lowestPriceInCentsForType, totalInCentsForItems } = useEmbroideryPrices()
  const {
    embroidery: { optionRows: copy, placement: placementCopy },
  } = useTranslation()

  const rows = EMBROIDERY_TYPES.map(typeKey => {
    const availability = matrix[typeKey]
    if (!availability.offered) return null

    const typeLabel = copy.typeLabels[typeKey]
    const typeItems = items.filter(item => item.typeKey === typeKey)

    if (typeItems.length === 0) {
      const offeredPlacementIds = availability.placements
        .filter(placement => placement.offered)
        .map(placement => placement.placementId)
      const fromPrice = formatPrice(lowestPriceInCentsForType(typeKey, offeredPlacementIds))

      return (
        <PanelOptionRow
          key={typeKey}
          label={typeLabel}
          subLabel={copy.typeSubLabel}
          priceLabel={fromPrice && copy.fromPrice.replace('{{price}}', fromPrice)}
          unavailableReason={
            availability.canAdd
              ? undefined
              : unavailabilityLabel(availability.reason ?? 'NO_PLACEMENT', placementCopy)
          }
          onOpen={() => onOpenType(typeKey)}
          openLabel={copy.addRow.replace('{{type}}', typeLabel)}
        />
      )
    }

    const removable = typeItems.filter(item => item.source !== 'ENFORCED')
    const addedPrice = formatPrice(totalInCentsForItems(typeItems))

    return (
      <PanelOptionRow
        key={typeKey}
        label={typeLabel}
        subLabel={copy.typeSubLabel}
        priceLabel={addedPrice && copy.addedPrice.replace('{{price}}', addedPrice)}
        editLabel={copy.edit}
        onOpen={() => onOpenType(typeKey)}
        openLabel={copy.editRow.replace('{{type}}', typeLabel)}
        onRemove={
          removable.length === 0
            ? undefined
            : () => removable.forEach(item => removeItem(item.instanceId))
        }
        removeLabel={copy.removeRow.replace('{{type}}', typeLabel)}
      />
    )
  })

  return <>{rows}</>
}
